/** 
 * @module PlanVerificationDetails
 * @description Parent component that renders plan verification details with tabs for
 * Plan Generator, Documents, Corrections, and Verification based on user claims and feature access.
 */
import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import { ClaimsContext } from "../../services/ClaimsService";
import { FeatureType } from "../../constants/FeatureToggles";
import PlanGenerator from "./PlanGenerator/PlanGenerator";
import PlanVerificationDocuments from "./Documents/Documents";
import PlanVerificationCorrections from "./Corrections/Corrections";
import PlanVerificationVerification from "./Verification/Verification";
import { ClaimType } from "../../constants/claims";
import { Button, COLORS, Modal, Tab, Icon } from "abs-ui-components";
import { PlanVerificationPageColumns } from "../../pages/PlanVerification/PlanVerificationPage/PlanVerificationPageColumns";
import { MaterialReactTable } from "material-react-table";
import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import ContainedLoader from "../../common/Loader/ContainedLoader";
import { SEEMPTYPE, AMENDMENTTYPE } from "./WaypointEnums";
/**
 * @param {object} props
 * @param {(tabId: string) => void} [props.onTabChange] optional callback when active tab changes
 */
const PlanVerificationDetails = (props) => {
	/* ----------------------------- Context ----------------------------- */
	const { userHasClaim, userHasFeature, userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta } = useContext(ClaimsContext);

	/* ------------------------------ Routing ---------------------------- */
	const params = useParams();
	const planId = params.planId;
	const location = useLocation();
	const wcnNumber = location.state?.wcnNumber;
	const shouldClone = location.state?.shouldClone;
	const navigate = useNavigate();

	/* ------------------------------ State ------------------------------ */
	const [planVerificationDetails, setPlanVerificationDetails] = useState(null);
	const [planVerificationFlags, setPlanVerificationFlags] = useState(null);
	const [associatedDocumentsData, setAssociatedDocumentsData] = useState([]);
	const [selectedTab, setActiveTab] = useState(0);
	const [authorized, setAuthorized] = useState(true);
	const [loading, setLoading] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [selectedWaypointAmendType, setSelectedWaypointAmendType] = useState("Major");
	const [selectedWaypointType, setSelectedWaypointType] = useState("New");

	// header/footer (breadcrumb) UI state with debounced setter
	const [showbreadCrumb, setShowBreadCrumb] = useState(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		return storedValue !== null ? JSON.parse(storedValue) : true;
	});
	const debouncedSetHeaderFooterEnabled = debounce((value) => {
		setShowBreadCrumb(value);
	}, 100);

	/* ---------------------------- Effects ------------------------------ */

	// Initialize header/footer state from session storage (debounced)
	useEffect(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		if (storedValue !== null) {
			debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
		}
	}, [debouncedSetHeaderFooterEnabled]);

	// Ensure user feature toggles are loaded and WCN aware
	useEffect(() => {
		if (!userFeaturesAreLoaded || checkWcnDelta(wcnNumber)) {
			fetchUserFeatures(wcnNumber);
		}
	}, [userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta, wcnNumber]);

	// Authorization check
	useEffect(() => {
		if (planId) {
			setAuthorized(getPlanVerificationDetailsByPlanType(planId, wcnNumber));
		}
	}, [planId, wcnNumber]);

	/* ----------------------- Feature Toggles: Plan Generator ----------------------- */

	const SEEMP2PlanGeneratorEnabled = useMemo(
		() => userFeaturesAreLoaded && (userHasFeature(FeatureType.CreateSeemp2MonitoringPlan) || userHasFeature(FeatureType.ViewSeemp2MonitoringPlan)),
		[userFeaturesAreLoaded, userHasFeature]
	);

	const SEEMP3PlanGeneratorEnabled = useMemo(
		() => userFeaturesAreLoaded && (userHasFeature(FeatureType.CreateSeemp3MonitoringPlan) || userHasFeature(FeatureType.ViewSeemp3MonitoringPlan)),
		[userFeaturesAreLoaded, userHasFeature]
	);

	const EUMRVPlanGeneratorEnabled = useMemo(
		() => userFeaturesAreLoaded && (userHasFeature(FeatureType.CreateMrvMonitoringPlan) || userHasFeature(FeatureType.ViewMrvMonitoringPlan)),
		[userFeaturesAreLoaded, userHasFeature]
	);

	const FEUMPPlanGeneratorEnabled = useMemo(
		() => userFeaturesAreLoaded && (userHasFeature(FeatureType.CreateFuelEuMonitoringPlan) || userHasFeature(FeatureType.ViewFuelEuMonitoringPlan)),
		[userFeaturesAreLoaded, userHasFeature]
	);

	const getFeatureTogglesForPlanGeneratorByPlanType = (planType) => {
		switch (planType) {
			case "SEEMP II":
				return SEEMP2PlanGeneratorEnabled;
			case "SEEMP III":
				return SEEMP3PlanGeneratorEnabled;
			case "EU MRV MP":
				return EUMRVPlanGeneratorEnabled;
			case "FEU MP":
				return FEUMPPlanGeneratorEnabled;
			default:
				return false;
		}
	};

	/* ----------------------- Feature Toggles: Documents ------------------------ */

	const SEEMP2DocumentsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewSEEMPIIMonitoringPlanDocuments),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const SEEMP3DocumentsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewSEEMPIIIMonitoringPlanDocuments),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const EUMRVMPDocumentsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewMRVMonitoringPlanDocuments),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const FEUMPDocumentsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewFEUMonitoringPlanDocuments),
		[userFeaturesAreLoaded, userHasFeature]
	);

	const getFeatureTogglesForDocumentsByPlanType = (planType) => {
		switch (planType) {
			case "SEEMP II":
				return SEEMP2DocumentsEnabled;
			case "SEEMP III":
				return SEEMP3DocumentsEnabled;
			case "EU MRV MP":
				return EUMRVMPDocumentsEnabled;
			case "FEU MP":
				return FEUMPDocumentsEnabled;
			default:
				return false;
		}
	};

	/* ----------------------- Feature Toggles: Corrections ---------------------- */

	const SEEMP2CorrectionsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewSEEMPIIMonitoringPlanCorrections),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const SEEMP3CorrectionsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewSEEMPIIIMonitoringPlanCorrections),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const EUMRVMPCorrectionsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewMRVMonitoringPlanCorrections),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const FEUMPCorrectionsEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewFEUMonitoringPlanCorrections),
		[userFeaturesAreLoaded, userHasFeature]
	);

	const getFeatureTogglesForCorrectionsByPlanType = (planType) => {
		switch (planType) {
			case "SEEMP II":
				return SEEMP2CorrectionsEnabled;
			case "SEEMP III":
				return SEEMP3CorrectionsEnabled;
			case "EU MRV MP":
				return EUMRVMPCorrectionsEnabled;
			case "FEU MP":
				return FEUMPCorrectionsEnabled;
			default:
				return false;
		}
	};

	/* ----------------------- Feature Toggles: Verification --------------------- */
	const SEEMP2VerificationEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewSEEMPIIMonitoringPlanVerification),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const SEEMP3VerificationEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewSEEMPIIIMonitoringPlanVerification),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const EUMRVMPVerificationEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewMRVMonitoringPlanVerification),
		[userFeaturesAreLoaded, userHasFeature]
	);
	const FEUMPVerificationEnabled = useMemo(
		() => userFeaturesAreLoaded && userHasFeature(FeatureType.ViewFEUMonitoringPlanVerification),
		[userFeaturesAreLoaded, userHasFeature]
	);

	const getFeatureTogglesForVerificationByPlanType = (planType) => {
		switch (planType) {
			case "SEEMP II":
				return SEEMP2VerificationEnabled;
			case "SEEMP III":
				return SEEMP3VerificationEnabled;
			case "EU MRV MP":
				return EUMRVMPVerificationEnabled;
			case "FEU MP":
				return FEUMPVerificationEnabled;
			default:
				return false;
		}
	};

	/* --------------------------- Data Fetchers --------------------------- */

	/**
	 * Fetches plan verification details which is then passed down to child components.
	 */
	const getPlanVerificationDetailsByPlanType = async (planId, wcnNumber) => {
		setLoading(true);
		await axiosHttp({
			url: `api/PlanVerification/GetPlanVerificationDetails?planId=${encodeURIComponent(planId)}&wcnNumber=${encodeURIComponent(wcnNumber)}`,
			method: "GET",
		})
			.then((response) => {
				setLoading(false);
				const data = response?.data;
				if (data) {
					setPlanVerificationDetails(data);
				}
			})
			.catch((error) => {
				setLoading(false);
				emitToasterMsg("error", error?.response?.data?.message || " Error fetching plan verification details");
			});
	};

	/**
	 * Fetches verification documents which is then passed down to Verification tab.
	 */
	const getVerificationDocuments = async (planId, wcnNumber) => {
		if (!planId || !userHasFeature(FeatureType.PlanVerificationDocuments)) {
			setAssociatedDocumentsData([]);
			return;
		}

		try {
			const query = `planId=${encodeURIComponent(planId)}&isVerification=true` + (wcnNumber ? `&wcnNumber=${encodeURIComponent(wcnNumber)}` : "");

			const res = await axiosHttp.get(`api/PlanVerification/GetDocuments?${query}`);

			if (res?.data?.isSuccess) {
				const payload = res?.data?.data;
				let documents = [];

				if (Array.isArray(payload)) {
					documents = payload;
				} else if (payload && typeof payload === "object") {
					if (Array.isArray(payload.documents)) {
						documents = payload.documents;
					} else if (Array.isArray(payload.documentReportInfos)) {
						documents = payload.documentReportInfos;
					}
				}

				setAssociatedDocumentsData(documents);
			} else {
				setAssociatedDocumentsData([]);
			}
		} catch (err) {
			console.error("GetDocuments (verification) failed:", err);
			setAssociatedDocumentsData([]);
		}
	};

	/**
	 * Fetches all plan verification flags which is then passed down to child components.
	 */
	const getPlanVerificationFlags = useCallback(async (planId, wcnNumber) => {
		try {
			const flagsUrl =
				`api/PlanVerification/GetPlanVerificationFlags?planId=${encodeURIComponent(planId)}` +
				(wcnNumber ? `&wcnNumber=${encodeURIComponent(wcnNumber)}` : "");

			const flagsResp = await axiosHttp({
				url: flagsUrl,
				method: "GET",
			});

			setPlanVerificationFlags(flagsResp?.data ?? null);
		} catch (err) {
			console.error("GetPlanVerificationFlags failed:", err);
			setPlanVerificationFlags(null);
		}
	}, []);

	/**
	 * Used as a callback to refresh plan verification details, flags, and documents,
	 * which then propagates down to child components.
	 */
	const refreshState = useCallback(async () => {
		if (!planId) return;
		try {
			await getPlanVerificationDetailsByPlanType(planId, wcnNumber);
			await getPlanVerificationFlags(planId, wcnNumber);
			await getVerificationDocuments(planId, wcnNumber);
			setRefreshKey((k) => k + 1);
		} catch (err) {
			emitToasterMsg("error", err?.response?.data?.message || "Error refreshing plan verification data");
		}
	}, [planId, wcnNumber, getPlanVerificationFlags]);

	// Initial and subsequent refetch when IDs change
	useEffect(() => {
		if (planId) {
			refreshState();
		}
	}, [planId, wcnNumber, refreshState]);

	// Keep flags in sync when dependencies change
	useEffect(() => {
		if (planId) {
			getPlanVerificationFlags(planId, wcnNumber);
		}
	}, [planId, wcnNumber, getPlanVerificationFlags]);

	/* ---------------------------- Handlers ---------------------------- */

	const handleOk = () => {
		navigate("/plan-verification");
	};

	const tabChangeHandler = (event) => {
		setActiveTab(event);
		if (filteredTabData[event]?.header) {
			props?.onTabChange(filteredTabData[event].header.toLowerCase());
		}
	};

	// Download XML template for WayPoint
	const handleDownloadforXMl = async () => {
		if (!selectedWaypointType) {
			emitToasterMsg("error", `Please select ${planVerificationDetails?.Plan || "SEEMP"} Type`);
			return;
		}

		if (selectedWaypointType === "ClientAmendment" && !selectedWaypointAmendType) {
			emitToasterMsg("error", "Please select Amendment Type");
			return;
		}
		if (!planVerificationDetails?.PlanId) {
			emitToasterMsg("error", "Missing plan id");
			return;
		}

		try {
			setLoading(true);
			const targetSeempInt = SEEMPTYPE[selectedWaypointType];
			const targetAmendInt = AMENDMENTTYPE[selectedWaypointAmendType];
			const targetPlanSegment = planVerificationDetails?.Plan === "SEEMP II" ? "seempii" : "seempiii";
			const exportUrl =
				`api/SeempMonitoring/${targetPlanSegment}/DownloadSeempXml/${encodeURIComponent(planVerificationDetails.PlanId)}` +
				`?seempType=${encodeURIComponent(targetSeempInt)}` +
				`&amendType=${encodeURIComponent(targetAmendInt)}`;

			const res = await axiosHttp({
				url: exportUrl,
				method: "GET",
				responseType: "blob",
			});

			const blob = new Blob([res.data], { type: "application/xml" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.download = "waypoint.xml";

			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			emitToasterMsg("success", "The XML file has been downloaded successfully.");
		} catch (error) {
			console.error("Download Error:", error);
		 const errorMessage =
          typeof error.response?.data === "string" ? error.response.data :
		  error.response?.data instanceof Blob ? await parseErrorBlob(error.response?.data)
		  : error.response?.data?.message || error.response?.data?.title || error.message;

          emitToasterMsg("error", errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const parseErrorBlob = async (blobFile) => {
		try {
			const errorText = await blobFile.text();

			try {
				const errorJson = JSON.parse(errorText);

				// Case 1: ASP.NET Validation dictionary error (e.g., ModelState validation failed)
				if (errorJson.errors && typeof errorJson.errors === 'object') {
					const firstErrorKey = Object.keys(errorJson.errors)[0];
					const errorMessages = errorJson.errors[firstErrorKey];

					if (Array.isArray(errorMessages) && errorMessages.length > 0) {
						return `${firstErrorKey}: ${errorMessages[0]}`;
					}
				}

				// Case 2: Standard JSON exception handling (e.g., your new "message" string error)
				return errorJson.message || errorJson.title || "An unexpected server error occurred.";

			} catch {
				// Case 3: Plain text raw strings or full HTML server crash pages
				return errorText || "An unexpected error occurred.";
			}
		} catch (blobError) {
			console.error("Failed to read error blob:", blobError);
			return "An unexpected error occurred.";
		}
	};

	/* --------------------------- Table Content --------------------------- */

	const columns = useMemo(() => PlanVerificationPageColumns, []);
	const tableData = planVerificationDetails ? [planVerificationDetails] : [];
	const tableElement = (
		<div className="mt-4">
			<MaterialReactTable
				columns={columns}
				data={tableData}
				enableTopToolbar={false}
				enableBottomToolbar={false}
				enableColumnFilters={false}
				enableSorting={false}
				enablePagination={false}
				enableRowSelection={false}
				enableExpanding={false}
				muiTableContainerProps={{ sx: { maxHeight: "none" } }}
			/>
		</div>
	);

	/* ----------------------------- Tabs ------------------------------ */

  const tabData = [
    {
      id: "details",
      header: "Plan Verification Details",
      content: <>{tableElement}</>
    },
    getFeatureTogglesForPlanGeneratorByPlanType(planVerificationDetails?.Plan) &&
    userHasClaim(ClaimType.VIEW_PLANVERIFICATION_PLANGENERATOR_TAB)
      ? {
          id: "generator",
          header: "Plan Generator",
          content: (
            <>
              {planVerificationDetails && (
                <PlanGenerator
                  planVerificationDetails={planVerificationDetails}
                  planVerificationFlags={planVerificationFlags}
                  shouldClone={shouldClone}
                  refreshState={refreshState}
                />
              )}
            </>
          )
        }
      : "",
    getFeatureTogglesForDocumentsByPlanType(planVerificationDetails?.Plan) &&
    userHasClaim(ClaimType.VIEW_PLANVERIFICATION_DOCUMENTS_TAB)
      ? {
          id: "documents",
          header: "Documents",
          content: (
            <>
              {planVerificationDetails && (
                <PlanVerificationDocuments
                  planVerificationDetails={planVerificationDetails}
                  planVerificationFlags={planVerificationFlags}
                  showData={showbreadCrumb}
                  onSubmittedToAbsSuccess={() =>
                    getPlanVerificationDetailsByPlanType(planId, wcnNumber)
                  }
                  refreshKey={refreshKey}
                  refreshState={refreshState}
                />
              )}
            </>
          )
        }
      : "",
    getFeatureTogglesForCorrectionsByPlanType(planVerificationDetails?.Plan) &&
    userHasClaim(ClaimType.VIEW_PLANVERIFICATION_CORRECTIONS_TAB)
      ? {
          id: "corrections",
          header: "Corrections",
          content: (
            <>
              {planVerificationDetails && (
                <PlanVerificationCorrections
                  planDetails={planVerificationDetails}
                  planVerificationFlags={planVerificationFlags}
                  userHasClaim={userHasClaim}
                  refreshState={refreshState}
                />
              )}
            </>
          )
        }
      : "",
    getFeatureTogglesForVerificationByPlanType(planVerificationDetails?.Plan) &&
    userHasClaim(ClaimType.VIEW_PLANVERIFICATION_VERIFICATION_TAB)
      ? {
          id: "verification",
          header: "Verification",
          content: (
            <>
              {planVerificationDetails && (
                <PlanVerificationVerification
                  planDetails={planVerificationDetails}
                  planVerificationFlags={planVerificationFlags}
                	selectedWaypointType={selectedWaypointType}
					setSelectedWaypointType={setSelectedWaypointType}
					selectedWaypointAmendType={selectedWaypointAmendType}
					setSelectedWaypointAmendType={setSelectedWaypointAmendType}
				/>
              )}
            </>
          )
        }
      : ""
  ];

	// Remove falsy entries (feature gated)
	const filteredTabData = tabData.filter(Boolean);

  // Inject active state and associated docs into Verification tab
  const filteredTabDataWithActive = filteredTabData.map((tab, idx) => {
    if (tab.header === "Verification") {
      return {
        ...tab,
        content: (
          <>
            {planVerificationDetails && (
              <PlanVerificationVerification
                planDetails={planVerificationDetails}
                planVerificationFlags={planVerificationFlags}
                isActive={selectedTab === idx}
                refreshState={refreshState}
                associatedDocumentsData={associatedDocumentsData}
                selectedWaypointType={selectedWaypointType}
				setSelectedWaypointType={setSelectedWaypointType}
				selectedWaypointAmendType={selectedWaypointAmendType}
				setSelectedWaypointAmendType={setSelectedWaypointAmendType}
			  />
            )}
          </>
        )
      };
    }
    return tab;
  });

	// Keep active tab index in bounds when tabs visibility changes
	useEffect(() => {
		if (selectedTab >= filteredTabData.length) {
			setActiveTab(0);
		}
	}, [filteredTabData.length]);

	/* ----------------------------- Render ----------------------------- */

	return (
		<>
			{authorized ? (
				<div>
					{loading && <ContainedLoader />}

					{/* Header */}
					<div className="mb-2 d-flex justify-content-between">
						<div className="planVerification-info">
							<span className="header">
								{planVerificationDetails?.Plan}-{planVerificationDetails?.ImoNumber}-{planVerificationDetails?.WcnNumber}
							</span>
							{planVerificationDetails?.ImoNumber ? (
								<span className="text-gray ml-4 fw-bolder">
									{planVerificationDetails?.VesselName}, IMO# {planVerificationDetails?.ImoNumber}
								</span>
							) : (
								<span className="text-gray ml-4 fw-bolder">{planVerificationDetails?.VesselName}</span>
							)}
						</div>

            <div className="d-flex">
              {planVerificationDetails && (
                <Button
                  variant="tertiary"
                  startIcon={<Icon icon="arrow_back" />}
                  className="mr-4"
                  onClick={() => {
                    navigate("/plan-verification");
                  }}
                >
                  Return to Plan Verification List
                </Button>
              )}
			   {userHasClaim(ClaimType.VIEW_VERIFICATION_TAB) &&
								(planVerificationDetails?.Plan === "SEEMP II" || planVerificationDetails?.Plan === "SEEMP III") &&
                planVerificationDetails?.Flag === "Liberia" &&
						 (
									<Button variant="tertiary" startIcon={<Icon icon="ios_share" />} className="mr-4" onClick={handleDownloadforXMl}>
										Download WayPoint XML
									</Button>
								)}
            </div>
          </div>

					{/* Tabs and content */}
					<div className="planVerification-details">
						{planVerificationDetails && (
							<>
								{filteredTabData.length > 0 ? (
									<Tab tabData={filteredTabDataWithActive} onTabChange={tabChangeHandler} activeTab={selectedTab} />
								) : (
									tableElement
								)}
							</>
						)}
					</div>
				</div>
			) : (
				<div>
					<Modal
						isOpen={!authorized}
						modalHeader
						padding={"70px"}
						headerHeight={"50px"}
						showFooter
						footer={
							<div className="d-flex justify-content-end fixed-action-btns p-4">
								<Button
									className="modal-delete-btn"
									onClick={handleOk}
									primaryColor={COLORS.RED.DARK}
									primaryBackgroundColor={COLORS.RED.LIGHT}
								>
									Ok
								</Button>
							</div>
						}
					>
						<div>You are not authorized to view this.</div>
					</Modal>
				</div>
			)}
		</>
	);
};;

export default PlanVerificationDetails;
